/**
 * ErrorState (TMMPDM-0034, TMM-B3, FEED-003 / ANTI-014): a region that could not load says what happened,
 * offers Try again when the region can re-read, and shows the server's request id as a reference
 * support can trace, in mono. Never the server's text, raw exception text or a bare error code
 * (TMMPDM-0063): the catalog copy for the failure's code when it has one (`errorCopy`), else the
 * screen's own catalog message, else a generic line.
 *
 * An error `Alert` (announced, `role="alert"`), so the look of today's load errors is kept.
 * TMMPDM-0036: `action` replaces Try again when re-reading can't help (Copy on a non-Draft card's
 * edit URL).
 *
 * `Retry-After` (TMM-B3): the vendored axios factory (`src/api/httpClientFactory.ts`, never edited
 * locally) does not put response headers on `ApiError`, so a 429's wait is not readable here and no
 * countdown is shown; a rate-limited failure says to wait a moment and still offers Try again. The
 * countdown lands when the fleet factory exposes the header.
 */
import type { ReactNode } from 'react';
import { Alert, Button, Typography } from '@mui/material';
import { ApiError, errorCopy } from '@/api/errors';
import { useCopy } from '@/hooks/useCopy';

export interface ErrorStateProps {
  /** The failure itself — read for its code (catalog copy) and its request id. */
  error: unknown;
  /** The screen's catalog message for this region ("Could not load tenants."), used when the
   *  failure's code has no copy of its own. */
  message?: string;
  onRetry?: () => void;
  /** The way out in place of Try again. */
  action?: ReactNode;
}

export function ErrorState({ error, message, onRetry, action }: ErrorStateProps) {
  const { t } = useCopy();
  const text = errorCopy(
    error,
    t,
    message ?? t('components.loadFailed', 'This could not be loaded.'),
  );
  const requestId = error instanceof ApiError ? error.requestId : undefined;
  return (
    <Alert
      severity="error"
      action={
        action ??
        (onRetry ? (
          <Button color="inherit" size="small" onClick={onRetry}>
            {t('common.tryAgain', 'Try again')}
          </Button>
        ) : undefined)
      }
    >
      {text}
      {requestId ? (
        <Typography
          variant="caption"
          component="p"
          sx={(theme) => ({ mt: 0.5, ...theme.typography.mono })}
        >
          {t('components.reference', 'Reference: {{id}}', { id: requestId })}
        </Typography>
      ) : null}
    </Alert>
  );
}
